import {
  batch,
  createContext,
  createEffect,
  createMemo,
  createResource,
  JSX,
  on,
  untrack,
  useContext,
} from 'solid-js';
import { useAuth } from '@trimble/trimble-id-solidjs';
import { useLogger } from '@trimble/solidjs-utils';
import { useConnectProject } from '@trimble/connect-project-providers';
import { FeatureScope, MapLayer, GeospatialFeature } from '@trimble/geospatial';

import { EXPERIMENTS_KEYS, useAppContext } from '../AppContextProvider';
import { useDataContext } from '../data-state/DataContextProvider';
import { getVisiblePointCloudIds } from '../data-state/DataContextProviderHelpers';
import { isFeatureExtractionEditingEligible } from './feature-extraction-eligibility';
import type {
  FeatureExtractionContextType,
  FeatureExtractionLayersLoadStatus,
} from './feature-extraction-context.types';
import {
  createDefaultFeatureExtractionState,
  createFeatureExtractionStateModel,
  FeatureExtractionState,
} from './feature-extraction-state-model';
import { createStore, reconcile } from 'solid-js/store';
import { createFeatureExtractionLoaders } from './feature-extraction-loaders';
import {
  createFeatureExtractionRepositories,
  createMapsRegion,
  resolveFxTilesServiceBaseUrl,
} from './feature-extraction-repositories';
import { createTileCache } from './feature-extraction-tile-cache';

export type {
  FeatureExtractionContextType,
  FeatureExtractionLayersLoadStatus,
} from './feature-extraction-context.types';

const FeatureExtractionContext = createContext<FeatureExtractionContextType>();

export interface FeatureExtractionContextProviderProps {
  children: JSX.Element;
  fxMapsServiceBaseUrl?: string;
  fxFeaturesServiceBaseUrl?: string;
  fxTilesServiceBaseUrl?: string;
}

export function FeatureExtractionContextProvider(
  props: FeatureExtractionContextProviderProps
): JSX.Element {
  const auth = useAuth();
  const loggerContext = useLogger();
  const logger = loggerContext.getLogger('FeatureExtraction');
  const appContext = useAppContext();
  const { selectedProjectId, selectedRegion } = useConnectProject();
  const dataContext = useDataContext();

  const mapsRegion = createMapsRegion(() => selectedRegion()?.location);
  const getAccessToken = (): Promise<string> => auth().getAccessTokenSilently();

  const [editingState, setEditingState] = createStore<FeatureExtractionState>(
    createDefaultFeatureExtractionState()
  );
  const { tileCacheToken, bumpTileCacheToken } = createTileCache();
  const {
    upsertFeatures,
    upsertFeature,
    mergeFeatureProperties,
    removeFeature,
    getSceneAnchors,
    setSceneAnchors,
    registerFeatureRetention,
    evictDistantFeatures,
    pointCloudCrs,
    setPointCloudCrs,
    isLayerVisible,
    toggleLayerVisibility,
    applyProjectLayers,
  } = createFeatureExtractionStateModel({
    editingState,
    setEditingState,
    bumpTileCacheToken,
  });

  // createResource so Solid owns lifecycle; .latest for sync readers (tile fetcher).
  const [accessToken] = createResource(auth, async (currentAuth) => {
    try {
      return await currentAuth.getAccessTokenSilently();
    } catch (error) {
      logger.error(
        'Failed to acquire access token',
        undefined,
        error instanceof Error ? error : new Error(String(error))
      );
      throw error;
    }
  });
  const authToken = (): string | undefined => accessToken.latest;

  const isFeatureExtractionEnabled = createMemo(
    () =>
      appContext.experiments()[EXPERIMENTS_KEYS['featureExtractionPOC']] ===
      true
  );

  const georeferencedVisiblePointCloudIds = createMemo(() => {
    const csibs = dataContext.loadedCsibs();
    return (getVisiblePointCloudIds(dataContext.dataState) ?? []).filter((id) =>
      csibs.has(id)
    );
  });

  const visiblePointCloudCount = createMemo(
    () => georeferencedVisiblePointCloudIds().length
  );

  const isEditingEligibleAccessor = createMemo(() =>
    isFeatureExtractionEditingEligible({
      isExperimentEnabled: isFeatureExtractionEnabled(),
      projectId: selectedProjectId(),
      visiblePointCloudCount: visiblePointCloudCount(),
    })
  );

  const scope = createMemo<FeatureScope | undefined>(() => {
    const projectId = selectedProjectId();
    return projectId ? { projectId } : undefined;
  });

  const selectedLayer = createMemo<MapLayer | undefined>(() => {
    const id = editingState.selectedLayerId;
    return id
      ? editingState.layers.find((layer) => layer.id === id)
      : undefined;
  });

  const featureIds = createMemo((): readonly string[] =>
    Object.keys(editingState.features)
  );

  const getFeature = (id: string): GeospatialFeature | undefined =>
    editingState.features[id];

  const repositories = createFeatureExtractionRepositories({
    envName: () => appContext.envName,
    region: mapsRegion,
    logger,
    getAccessToken,
    fxMapsServiceBaseUrl: () => props.fxMapsServiceBaseUrl,
    fxFeaturesServiceBaseUrl: () => props.fxFeaturesServiceBaseUrl,
  });

  const tilesBaseUrl = createMemo(() => {
    const region = mapsRegion();
    if (region === undefined) {
      return '';
    }
    return resolveFxTilesServiceBaseUrl(
      props.fxTilesServiceBaseUrl,
      region,
      appContext.envName
    );
  });

  const { layersFetchKey, layersQuery, supplementFetchKey, supplementQuery } =
    createFeatureExtractionLoaders({
      isFeatureExtractionEnabled,
      selectedProjectId,
      authToken,
      mapsRegion,
      georeferencedVisiblePointCloudIds,
      repositories,
      logger,
    });

  const clearContext = (): void => {
    batch(() => {
      setEditingState(reconcile(createDefaultFeatureExtractionState()));
      bumpTileCacheToken();
    });
  };

  const resetEditingState = (): void => {
    if (editingState.selectedFeature === undefined) {
      return;
    }
    setEditingState('selectedFeature', undefined);
  };

  const layersLoadStatus = createMemo((): FeatureExtractionLayersLoadStatus => {
    if (layersFetchKey() === undefined) {
      return 'idle';
    }
    if (layersQuery.isFetching) {
      return 'pending';
    }
    if (layersQuery.isError) {
      return editingState.initialized ? 'success' : 'error';
    }
    return editingState.initialized ? 'success' : 'pending';
  });

  createEffect(
    on([selectedProjectId, mapsRegion], ([projectId, region], previous) => {
      const [previousProjectId, previousRegion] = previous ?? [];
      if (projectId !== previousProjectId || region !== previousRegion) {
        clearContext();
      }
    })
  );

  createEffect(() => {
    if (layersFetchKey() === undefined) {
      if (untrack(() => editingState.initialized)) {
        clearContext();
      }
      return;
    }

    if (!layersQuery.isSuccess) {
      return;
    }

    const data = layersQuery.data;
    if (data === undefined) {
      return;
    }
    untrack(() => applyProjectLayers(data));
  });

  createEffect(() => {
    if (supplementFetchKey() === undefined) {
      if (editingState.projectPointCloudCrs !== undefined) {
        setEditingState('projectPointCloudCrs', undefined);
      }
      return;
    }

    if (!supplementQuery.isSuccess) {
      return;
    }

    const data = supplementQuery.data;
    if (data === undefined || !data.loaded) {
      return;
    }
    if (data.pointCloudCrs !== editingState.projectPointCloudCrs) {
      setEditingState('projectPointCloudCrs', data.pointCloudCrs);
    }
  });

  const value: FeatureExtractionContextType = {
    isFeatureExtractionEnabled,
    isEditingEligible: isEditingEligibleAccessor,
    layersLoadStatus,
    layerRepository: () => repositories()?.layerRepository,
    featureRepository: () => repositories()?.featureRepository,
    scope,
    editingState,
    isLayerVisible,
    toggleLayerVisibility,
    setSelectedLayerId: (id: string | undefined) => {
      if (editingState.selectedLayerId === id) {
        return;
      }
      setEditingState('selectedLayerId', id);
    },
    selectedLayer,
    setSelectedFeature: (feature: GeospatialFeature | undefined) => {
      if (editingState.selectedFeature === feature) {
        return;
      }
      setEditingState('selectedFeature', feature);
    },
    getFeature,
    featureIds,
    getSceneAnchors,
    setSceneAnchors,
    registerFeatureRetention,
    evictDistantFeatures,
    upsertFeatures,
    upsertFeature,
    mergeFeatureProperties,
    removeFeature,
    pointCloudCrs,
    setPointCloudCrs,
    authToken,
    tileCacheToken,
    bumpTileCacheToken,
    tilesBaseUrl,
    reset: resetEditingState,
  };

  return (
    <FeatureExtractionContext.Provider value={value}>
      {props.children}
    </FeatureExtractionContext.Provider>
  );
}

export function useFeatureExtractionContext(): FeatureExtractionContextType {
  const context = useContext(FeatureExtractionContext);
  if (context === undefined) {
    throw new Error(
      'useFeatureExtractionContext must be used within a FeatureExtractionContextProvider'
    );
  }
  return context;
}
